/**
 * เวลาไทย (ICT, UTC+7) สำหรับหน้าจอ Tax Planning V2
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 ปัญหาที่ไฟล์นี้แก้ — มัน **ไม่ใช่** เรื่อง option ของตัว format แต่เป็นเรื่อง *การ parse*
 *
 * ทุกคอลัมน์เวลาของ Tax V2 ถูกเขียนด้วย `DateTime.UtcNow` ลง `datetime2`
 * (`TaxFormV2Repository.cs`, `TaxFormAnswerV2Repository.cs`, `TaxPlaygroundTurnV2Repository.cs`,
 *  และ `LogMessageBAL.cs` สำหรับแถว `TYPE_SEND='D'` ที่ Tax Log อ่าน) — `datetime2` ไม่มี timezone
 * ⇒ EF materialize กลับมาเป็น `DateTimeKind.Unspecified` และ `WebAPI/Startup.cs` ไม่เคยตั้งค่า
 * JSON serializer เลย ⇒ System.Text.Json ส่งออกมาเป็น `"2026-08-19T03:12:45.123"` **ไม่มี `Z`**
 *
 * ตามสเปก ECMAScript สตริง date-**time** ที่ไม่มี offset จะถูก parse เป็น **เวลาท้องถิ่น**
 * ⇒ `new Date(v)` ไม่แปลงอะไรเลย แล้วตัวเลข UTC ดิบ ๆ ถูกพิมพ์ออกจอเหมือนเป็นเวลาไทย = **ช้าไป 7 ชั่วโมง**
 * ⇒ การเติม `{ timeZone: 'Asia/Bangkok' }` เฉย ๆ **แก้ไม่ได้** เพราะค่าถูก parse ผิดไปตั้งแต่ต้นทางแล้ว
 *   ต้อง "ปัก" ให้มันเป็น UTC ก่อน parse (นั่นคือหน้าที่ของ {@link parseUtcDate}) แล้วค่อย format ที่ ICT
 *
 * แพตเทิร์นเดียวกับที่ `views/SocialApiLog.vue` (`formatDate`) แก้ไว้แล้วด้วย `moment.utc(v).local()`
 * ที่นี่ไม่ใช้ moment เพื่อไม่ให้ chunk ของ Tax V2 ต้องลาก moment ทั้งก้อนเข้ามาใหม่ และเพื่อ **ปักที่
 * Asia/Bangkok ตรง ๆ** ไม่ใช่ `.local()` — เจ้าของสั่งว่า "ให้เป็นเวลาไทย" ⇒ เครื่องที่ตั้ง timezone อื่น
 * ก็ต้องเห็นเวลาไทย ไม่ใช่เวลาเครื่องตัวเอง
 *
 * 🔴 ห้ามใช้กับค่าที่ **ไม่ได้** มาจาก wire ของ backend: ค่าที่ฝั่ง client สร้างเองด้วย
 * `new Date().toISOString()` มี `Z` ติดมาอยู่แล้ว — {@link parseUtcDate} รองรับได้ (ไม่เติม `Z` ซ้ำ)
 * แต่ค่าที่เป็นสตริงที่ format แล้ว (เช่น `"19/08/2026"`) จะ parse ไม่ผ่านและคืน fallback
 */

/** ประเทศไทยเป็น UTC+7 คงที่ ไม่มี DST มาตั้งแต่ปี 1955 ⇒ offset นี้ไม่มีวันเปลี่ยนตามฤดู */
export const THAI_TIME_ZONE = 'Asia/Bangkok';

/**
 * ISO date-time ที่ไม่มีตัวบอก timezone ต่อท้าย — รูปแบบที่ System.Text.Json ส่งออกมาสำหรับ
 * `DateTimeKind.Unspecified` · ยอมรับทั้งตัวคั่น `T` และช่องว่าง และเศษวินาทีจะมีหรือไม่มีก็ได้
 */
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * แปลงค่าจาก wire ให้เป็น `Date` ที่ชี้ "จุดเวลา" ที่ถูกต้องจริง ๆ
 *
 * - สตริง date-time ที่ **ไม่มี** zone ⇒ เติม `Z` (backend หมายถึง UTC เสมอ — ดูหัวไฟล์)
 * - สตริงที่มี `Z` / `+07:00` อยู่แล้ว ⇒ ปล่อยผ่าน ไม่แตะ
 * - สตริงวันที่ล้วน `YYYY-MM-DD` ⇒ ปล่อยผ่าน (สเปกบอกให้ parse เป็น UTC midnight อยู่แล้ว
 *   และ +7 ชั่วโมงยังอยู่ในวันเดิม ⇒ วันที่ไม่เลื่อน)
 * - `Date` ⇒ ใช้ตามนั้น
 *
 * @returns {Date|null} `null` เมื่อว่าง / parse ไม่ได้ — ผู้เรียกเป็นคนตัดสินใจว่าจะแสดงอะไรแทน
 */
export function parseUtcDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (!raw) return null;

  const iso = ZONELESS_DATETIME.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// `Intl.DateTimeFormat` มีราคาในการสร้างพอสมควร และหน้าจอพวกนี้เรียก formatter เดิมซ้ำหลักร้อยครั้ง
// ต่อการ render หนึ่งครั้ง (ทุกบับเบิลในแชท, ทุกแถวในตาราง) ⇒ cache ตาม option ที่ใช้จริง
const formatterCache = new Map();

function formatterFor(options) {
  const key = JSON.stringify(options);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    // 'en-GB' เป็นแค่ฐานตัวเลข (เลขอารบิก + ปี ค.ศ.) — ผลลัพธ์ถูกประกอบเองจาก parts ทั้งหมด
    // จึงไม่มีข้อความของ locale หลุดออกไป · `hourCycle: 'h23'` = 00–23 (ไม่ใช่ 24:xx ของ 'h24')
    formatter = new Intl.DateTimeFormat('en-GB', { timeZone: THAI_TIME_ZONE, ...options });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/** ชิ้นส่วนของเวลาไทย เป็น object `{ year, month, day, hour, minute, second }` */
function thaiParts(date, options) {
  const out = {};
  for (const part of formatterFor(options).formatToParts(date)) out[part.type] = part.value;
  return out;
}

const DATE_TIME_OPTIONS = {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
};
const TIME_OPTIONS = { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' };
const DATE_OPTIONS = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** `DD/MM/YYYY HH:mm` เวลาไทย — ปี ค.ศ. (รูปแบบเดิมของตาราง Tax Log) */
export function fmtThaiDateTime(value, fallback = '—') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_TIME_OPTIONS);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** `DD/MM/YYYY HH:mm` เวลาไทย — ปี **พ.ศ.** ทุกภาษา (คอลัมน์สร้าง/อัปเดตของ Tax History V2, #53-PRD) */
export function fmtThaiDateTimeBE(value, fallback = '—') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_TIME_OPTIONS);
  return `${p.day}/${p.month}/${Number(p.year) + 543} ${p.hour}:${p.minute}`;
}

/** `HH:mm:ss` เวลาไทย — สำหรับเวลาใต้บับเบิลแชทและแถบเทิร์น */
export function fmtThaiTime(value, fallback = '') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, TIME_OPTIONS);
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** `YYYY-MM-DD HH:mm` เวลาไทย — เรียงตามตัวอักษรได้ (รูปแบบเดิมของ dialog ตั้งค่า Tax Planning V2) */
export function fmtThaiDateTimeIso(value, fallback = '') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_TIME_OPTIONS);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/** `YYYY-MM-DD` ตามวันที่ของ **เวลาไทย** (ไม่ใช่วัน UTC ซึ่งเลื่อนไปหลัง 17:00 น. บ้านเรา) */
export function fmtThaiDate(value, fallback = '—') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_OPTIONS);
  return `${p.year}-${p.month}-${p.day}`;
}

/** ชื่อเดือนแบบย่อของไทย — index 0 = มกราคม (ตรงกับ `month` ที่ `thaiParts` คืนมาเป็น '01'–'12') */
const THAI_MONTH_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

/** หนึ่งวันเป็นมิลลิวินาที — ไทยไม่มี DST ⇒ ลบ 24 ชม. แล้วได้ "เมื่อวาน" ตรงเสมอ (ดูหัวไฟล์) */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `2 ก.ย. 2026` — วันที่แบบไทยอ่านง่าย **มีปีเสมอ** เวลาไทย
 *
 * 🔴 มีไว้สำหรับ **หัวคั่นในแชท** (`TaxLogChatPanelV2`) ซึ่งเป็นข้อความที่คนอ่านเพื่อ *อ้างอิงวัน* ไม่ใช่
 * เพื่อเทียบว่าใหม่แค่ไหน ⇒ ต่างจาก {@link fmtThaiDayTime} ตรงที่ **ไม่** พูดว่า "วันนี้ / เมื่อวาน" และ
 * **ไม่** ตัดปีทิ้งเมื่อเป็นปีปัจจุบัน: log ที่เปิดค้างข้ามวัน หรือแคปหน้าจอส่งต่อ ต้องยังอ่านออกว่าวันไหน
 */
export function fmtThaiDayLabel(value, fallback = '—') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_TIME_OPTIONS);
  const month = THAI_MONTH_ABBR[Number(p.month) - 1] || p.month;
  return `${Number(p.day)} ${month} ${p.year}`;
}

/** `14:32` เวลาไทย — เวลาแบบไม่มีวินาที (คู่กับ {@link fmtThaiDayLabel} บนหัวคั่นในแชท) */
export function fmtThaiHourMinute(value, fallback = '') {
  const date = parseUtcDate(value);
  if (!date) return fallback;
  const p = thaiParts(date, DATE_TIME_OPTIONS);
  return `${p.hour}:${p.minute}`;
}

/**
 * `วันนี้ 13:16` / `เมื่อวาน 09:28` / `31 ส.ค. 16:22` / `31 ส.ค. 2025 16:22` — เวลาไทย
 *
 * 🔴 มีไว้สำหรับ **รายการที่ต้องกวาดตาเทียบกันหลายแถว** (เช่น ตัวเลือกฟอร์มในหน้า Tax Log ที่หนึ่ง
 * session มีได้ 32 ฟอร์ม) ซึ่ง `DD/MM/YYYY HH:mm` ใช้ไม่ได้ผล เพราะทุกแถวขึ้นต้นด้วย `01/09/2026`
 * เหมือนกันหมด ตัวที่ต่างกันจริงถูกดันไปอยู่ท้ายสุด สายตาเลยอ่านว่า "เวลาซ้ำ ๆ กัน"
 * ⇒ รูปแบบนี้ย้ายส่วนที่ต่างมาไว้ **ต้นข้อความ** และตัดปีทิ้งเมื่อเป็นปีเดียวกับวันนี้
 *
 * ⚠️ อย่าใช้กับคอลัมน์ในตารางที่ต้องเรียง/คัดลอกไปใช้ต่อ — ใช้ {@link fmtThaiDateTime} ตามเดิม
 *
 * @param {Date} [now] จุดอ้างอิงของคำว่า "วันนี้" (มีไว้ให้เทสต์ล็อกเวลาได้)
 */
export function fmtThaiDayTime(value, fallback = '—', now = new Date()) {
  const date = parseUtcDate(value);
  if (!date) return fallback;

  const p = thaiParts(date, DATE_TIME_OPTIONS);
  const time = `${p.hour}:${p.minute}`;

  // เทียบเป็น "วันตามเวลาไทย" ไม่ใช่วันของเครื่อง — หลัง 17:00 น. บ้านเรา วัน UTC เลื่อนไปแล้ว
  const day = fmtThaiDate(date, '');
  const today = fmtThaiDate(now, '');
  if (day && day === today) return `วันนี้ ${time}`;
  if (day && day === fmtThaiDate(new Date(now.getTime() - ONE_DAY_MS), '')) return `เมื่อวาน ${time}`;

  const month = THAI_MONTH_ABBR[Number(p.month) - 1] || p.month;
  const sameYear = Boolean(today) && today.slice(0, 4) === day.slice(0, 4);
  return sameYear
    ? `${Number(p.day)} ${month} ${time}`
    : `${Number(p.day)} ${month} ${p.year} ${time}`;
}

/**
 * อายุของเวลาแบบคร่าว ๆ: `เมื่อครู่` / `5 นาทีที่แล้ว` / `2 ชม.ที่แล้ว` / `3 วันที่แล้ว`
 *
 * 🔴 เป็น **ของประกอบ** เท่านั้น ต้องมีเวลาจริงแสดงคู่กันเสมอ — ค่านี้คำนวณตอน render และไม่ได้
 * อัปเดตตัวเองตามเวลาที่เดินไป ถ้าเปิดจอค้างไว้นานมันจะเก่า
 *
 * คืน `fallback` เมื่อ parse ไม่ได้ / เวลาอยู่ในอนาคต (นาฬิกาเครื่องเพี้ยน) / เก่ากว่า 30 วัน —
 * กรณีพวกนั้นเวลาจริงที่แสดงคู่กันอ่านง่ายกว่าอยู่แล้ว
 *
 * @param {Date} [now] จุดอ้างอิง (มีไว้ให้เทสต์ล็อกเวลาได้)
 */
export function fmtThaiRelative(value, fallback = '', now = new Date()) {
  const date = parseUtcDate(value);
  if (!date) return fallback;

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return fallback;

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'เมื่อครู่';
  if (minutes < 60) return `${minutes} นาทีที่แล้ว`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ชม.ที่แล้ว`;

  const days = Math.floor(hours / 24);
  return days <= 30 ? `${days} วันที่แล้ว` : fallback;
}
